'use client'
import * as React from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '../lib/utils'

/**
 * `ResponsiveTable` / `DataList` (FO-060) — één kolomdefinitie, twee weergaven:
 * op `md+` een tabel (met `overflow-x-auto` + optioneel een vastgezette eerste
 * kolom), op mobiel een kaartweergave (label/waarde-paren) zodat de drukke
 * backoffice-lijsten leesbaar blijven op 375px zonder mini-tabel-horizontaal-scroll.
 *
 * Puur presentatie: data, sortering, selectie en rij-acties komen via props/cellen
 * binnen, zodat bestaande lijsten zonder functionele wijziging kunnen overstappen.
 */

export interface ResponsiveColumn<T> {
  /** Unieke sleutel van de kolom. */
  key: string
  /** Kop: desktop-`<th>` én (default) het label in de mobiele kaart. */
  header: React.ReactNode
  /** Celinhoud voor een rij (hergebruik de bestaande cel-JSX). */
  cell: (row: T) => React.ReactNode
  /** Extra classes op de desktop-`<td>`. */
  className?: string
  /** Extra classes op de desktop-`<th>`. */
  headerClassName?: string
  /** Uitlijning van de cel (desktop + kaartwaarde). */
  align?: 'left' | 'center' | 'right'
  /** Laat deze kolom weg uit de mobiele kaartweergave (bv. een actie-kolom). */
  hideOnMobile?: boolean
  /** Toon als titel van de kaart i.p.v. als label/waarde-paar. */
  primary?: boolean
  /** Afwijkend label in de kaartweergave (default: `header`). */
  cardLabel?: React.ReactNode
  /**
   * Maak de desktop-kop sorteerbaar. Generiek en ontkoppeld van de sorteer-hook:
   * geef de actieve/richting-status + een toggle mee (bv. uit `useSortableList`).
   */
  sort?: { active: boolean; dir: 'asc' | 'desc'; onToggle: () => void }
}

export interface ResponsiveTableSelection<T> {
  isSelected: (row: T) => boolean
  onToggle: (row: T) => void
  allSelected?: boolean
  someSelected?: boolean
  onToggleAll?: () => void
  ariaLabel?: (row: T) => string
}

export interface ResponsiveTableProps<T> {
  columns: ResponsiveColumn<T>[]
  rows: readonly T[]
  /** Stabiele React-key per rij. */
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  /** Extra classes per rij (zowel `<tr>` als kaart). */
  rowClassName?: (row: T) => string | undefined
  /** Zet de eerste kolom vast bij horizontaal scrollen (desktop). */
  stickyFirstColumn?: boolean
  /** Optionele selectie-kolom (checkbox) vooraan. */
  selection?: ResponsiveTableSelection<T>
  /** Wat te tonen als er geen rijen zijn. */
  empty?: React.ReactNode
  /** Acties onderaan elke kaart (mobiel). */
  cardActions?: (row: T) => React.ReactNode
  className?: string
  tableClassName?: string
}

const alignText = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const

export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  stickyFirstColumn = false,
  selection,
  empty,
  cardActions,
  className,
  tableClassName,
}: ResponsiveTableProps<T>) {
  const cardColumns = columns.filter((c) => !c.hideOnMobile)
  const primaryCol = cardColumns.find((c) => c.primary)
  const detailCols = cardColumns.filter((c) => !c.primary)

  if (rows.length === 0 && empty !== undefined) {
    return <div className={className}>{empty}</div>
  }

  return (
    <div className={className}>
      {/* Desktop (md+): tabel met horizontale scroll + optioneel sticky 1e kolom. */}
      <div className="hidden overflow-x-auto md:block">
        <table className={cn('w-full text-sm', tableClassName)}>
          <thead>
            <tr className="border-b border-streep-border text-left text-streep-text-muted">
              {selection && (
                <th className="w-10 px-2 py-2">
                  {selection.onToggleAll && (
                    <input
                      type="checkbox"
                      aria-label="Alles selecteren"
                      checked={selection.allSelected ?? false}
                      ref={(el) => {
                        if (el) el.indeterminate = !!selection.someSelected && !selection.allSelected
                      }}
                      onChange={selection.onToggleAll}
                      className="h-4 w-4"
                    />
                  )}
                </th>
              )}
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-3 py-2 font-medium',
                    col.align && alignText[col.align],
                    stickyFirstColumn && i === 0 && 'sticky left-0 z-[1] bg-streep-surface',
                    col.headerClassName,
                  )}
                >
                  {col.sort ? (
                    <button
                      type="button"
                      onClick={col.sort.onToggle}
                      className={cn(
                        'inline-flex items-center gap-1.5 font-medium hover:text-streep-dark',
                        col.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      <span>{col.header}</span>
                      {!col.sort.active ? (
                        <ArrowUpDown className="h-3.5 w-3.5 text-streep-text-muted" />
                      ) : col.sort.dir === 'asc' ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-streep-border/60 bg-streep-surface',
                  onRowClick && 'cursor-pointer hover:bg-streep-surface-alt',
                  rowClassName?.(row),
                )}
              >
                {selection && (
                  <td className="w-10 px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={selection.ariaLabel?.(row) ?? 'Rij selecteren'}
                      checked={selection.isSelected(row)}
                      onChange={() => selection.onToggle(row)}
                      className="h-4 w-4"
                    />
                  </td>
                )}
                {columns.map((col, i) => (
                  <td
                    key={col.key}
                    className={cn(
                      'px-3 py-2',
                      col.align && alignText[col.align],
                      stickyFirstColumn && i === 0 && 'sticky left-0 z-[1] bg-inherit',
                      col.className,
                    )}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobiel (< md): kaartweergave uit dezelfde kolomdefinitie. */}
      <div className="space-y-3 md:hidden">
        {rows.map((row) => {
          const clickable = !!onRowClick
          return (
            <div
              key={rowKey(row)}
              onClick={clickable ? () => onRowClick?.(row) : undefined}
              className={cn(
                'rounded-lg border border-streep-border bg-streep-surface p-4 shadow-sm',
                clickable && 'cursor-pointer active:bg-streep-surface-alt',
                rowClassName?.(row),
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {primaryCol && (
                    <div className="font-medium text-streep-dark">{primaryCol.cell(row)}</div>
                  )}
                </div>
                {selection && (
                  <input
                    type="checkbox"
                    aria-label={selection.ariaLabel?.(row) ?? 'Rij selecteren'}
                    checked={selection.isSelected(row)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => selection.onToggle(row)}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                )}
              </div>
              <dl className="mt-2 grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-1 text-sm">
                {detailCols.map((col) => (
                  <React.Fragment key={col.key}>
                    <dt className="text-streep-text-muted">{col.cardLabel ?? col.header}</dt>
                    <dd
                      className={cn(
                        'min-w-0 text-streep-dark',
                        col.align === 'right' ? 'text-right' : 'text-left',
                      )}
                    >
                      {col.cell(row)}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
              {cardActions && <div className="mt-3 flex flex-wrap gap-2">{cardActions(row)}</div>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Alias — `DataList` en `ResponsiveTable` zijn hetzelfde component (FO-060). */
export const DataList = ResponsiveTable
